import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface WebVitalRow {
  name:       string;
  value:      number;
  rating:     string;
  url:        string;
  created_at: string;
}

interface MetricSummary {
  name:    string;
  avg:     number;
  count:   number;
  good:    number;
  poor:    number;
}

type DateRange = '7d' | '30d' | '90d';

const DATE_RANGE_DAYS: Record<DateRange, number> = {
  '7d':  7,
  '30d': 30,
  '90d': 90,
};

const METRIC_LABELS: Record<string, string> = {
  LCP:  'LCP',
  FID:  'FID / INP',
  INP:  'FID / INP',
  CLS:  'CLS',
  FCP:  'FCP',
  TTFB: 'TTFB',
};

const METRIC_ORDER = ['LCP', 'FID', 'INP', 'CLS', 'FCP', 'TTFB'];

const RATING_COLORS: Record<string, string> = {
  good:              '#22c55e',
  'needs-improvement': '#f97316',
  poor:              '#ef4444',
};

function formatValue(name: string, value: number): string {
  if (name === 'CLS') return value.toFixed(3);
  return `${Math.round(value)} ms`;
}

function RatingBadge({ rating }: { rating: string }) {
  const color = RATING_COLORS[rating] ?? '#9ca3af';
  return (
    <span
      style={{
        display:      'inline-block',
        padding:      '0.15rem 0.5rem',
        borderRadius: '999px',
        backgroundColor: color + '22',
        color,
        fontSize:     'var(--text-caption)',
        fontWeight:   600,
        textTransform: 'capitalize',
      }}
    >
      {rating === 'needs-improvement' ? 'Melhorar' : rating === 'good' ? 'Bom' : 'Ruim'}
    </span>
  );
}

function MetricCard({ summary }: { summary: MetricSummary }) {
  const label    = METRIC_LABELS[summary.name] ?? summary.name;
  const goodRate = summary.count > 0 ? Math.round((summary.good / summary.count) * 100) : 0;

  return (
    <div
      className="card"
      style={{ flex: '1 1 160px' }}
    >
      <p
        style={{
          fontSize:      'var(--text-caption)',
          color:         'var(--text-secondary)',
          margin:        '0 0 0.4rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight:    600,
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize:   '1.5rem',
          fontWeight: 700,
          color:      'var(--text-primary)',
          margin:     '0 0 0.3rem',
          fontFamily: 'var(--font-display)',
        }}
      >
        {summary.count === 0 ? '-' : formatValue(summary.name, summary.avg)}
      </p>
      {summary.count > 0 && (
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: 0 }}>
          <span style={{ color: '#22c55e', fontWeight: 600 }}>{goodRate}%</span> boas · {summary.count} amostras
        </p>
      )}
      {summary.count === 0 && (
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: 0, fontStyle: 'italic' }}>
          Sem dados
        </p>
      )}
    </div>
  );
}

export function AdminPerformancePage() {
  const [range,       setRange]       = useState<DateRange>('7d');
  const [summaries,   setSummaries]   = useState<MetricSummary[]>([]);
  const [poorRows,    setPoorRows]    = useState<WebVitalRow[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [tableAbsent, setTableAbsent] = useState(false);

  useEffect(() => { loadData(range); }, [range]);

  async function loadData(selectedRange: DateRange) {
    setLoading(true);
    setTableAbsent(false);

    const days      = DATE_RANGE_DAYS[selectedRange];
    const startDate = new Date(Date.now() - days * 86_400_000);

    // Load aggregate data
    const { data: metricData, error: metricErr } = await supabase
      .from('web_vitals')
      .select('name, value, rating')
      .gte('created_at', startDate.toISOString());

    if (metricErr) {
      // 42P01 = table does not exist; PGRST116 may also surface for missing tables
      const code = (metricErr as { code?: string }).code ?? '';
      if (code === '42P01' || code.includes('42P01') || metricErr.message?.includes('does not exist')) {
        setTableAbsent(true);
        setSummaries([]);
        setPoorRows([]);
        setLoading(false);
        return;
      }
      // Other error, show empty state gracefully
      setSummaries([]);
      setPoorRows([]);
      setLoading(false);
      return;
    }

    // Build per-metric summaries
    const buckets: Record<string, { values: number[]; good: number; poor: number }> = {};
    for (const row of (metricData ?? [])) {
      if (!buckets[row.name]) buckets[row.name] = { values: [], good: 0, poor: 0 };
      buckets[row.name].values.push(row.value);
      if (row.rating === 'good') buckets[row.name].good++;
      if (row.rating === 'poor') buckets[row.name].poor++;
    }

    const built: MetricSummary[] = METRIC_ORDER
      .filter(name => buckets[name])
      .map(name => {
        const b   = buckets[name];
        const avg = b.values.reduce((s, v) => s + v, 0) / b.values.length;
        return { name, avg, count: b.values.length, good: b.good, poor: b.poor };
      });

    // Add metrics that appear in data but not in METRIC_ORDER
    for (const name of Object.keys(buckets)) {
      if (!built.find(s => s.name === name)) {
        const b   = buckets[name];
        const avg = b.values.reduce((s, v) => s + v, 0) / b.values.length;
        built.push({ name, avg, count: b.values.length, good: b.good, poor: b.poor });
      }
    }

    setSummaries(built);

    // Load recent poor vitals
    const { data: poorData, error: poorErr } = await supabase
      .from('web_vitals')
      .select('name, value, rating, url, created_at')
      .eq('rating', 'poor')
      .order('created_at', { ascending: false })
      .limit(20);

    if (!poorErr) {
      setPoorRows((poorData ?? []) as WebVitalRow[]);
    }

    setLoading(false);
  }

  return (
    <div>
      <AdminPageHeader
        title="Performance Web"
        objective="Monitora as Core Web Vitals coletadas em produção: LCP, FID/INP, CLS, FCP e TTFB. Use para identificar páginas lentas e priorizar otimizações de performance."
        icon="⚡"
      />

      {/* Date range selector */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {(['7d', '30d', '90d'] as DateRange[]).map(r => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={range === r ? 'btn btn-primary' : 'btn'}
            style={{ minWidth: '60px' }}
          >
            {r === '7d' ? '7 dias' : r === '30d' ? '30 dias' : '90 dias'}
          </button>
        ))}
      </div>

      {/* Empty state when table doesn't exist */}
      {tableAbsent && (
        <div
          className="card"
          style={{
            textAlign:  'center',
            padding:    '3rem 2rem',
            color:      'var(--text-secondary)',
          }}
        >
          <p style={{ fontSize: '2rem', margin: '0 0 0.75rem' }}>📊</p>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>
            Tabela ainda não existe
          </p>
          <p style={{ fontSize: 'var(--text-caption)', margin: 0, fontStyle: 'italic' }}>
            Métricas serão exibidas após a tabela ser criada
          </p>
        </div>
      )}

      {/* Loading shimmer */}
      {loading && !tableAbsent && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="card shimmer" style={{ flex: '1 1 160px', height: '100px' }} />
          ))}
        </div>
      )}

      {/* Metric KPI cards */}
      {!loading && !tableAbsent && (
        <>
          {summaries.length === 0 ? (
            <div
              className="card"
              style={{
                textAlign: 'center',
                padding:   '2.5rem 2rem',
                color:     'var(--text-secondary)',
                marginBottom: '1.5rem',
              }}
            >
              <p style={{ margin: 0, fontStyle: 'italic', fontSize: 'var(--text-caption)' }}>
                Nenhuma métrica coletada no período selecionado.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {summaries.map(s => (
                <MetricCard key={s.name} summary={s} />
              ))}
            </div>
          )}

          {/* Recent poor vitals table */}
          <div className="card">
            <p
              style={{
                fontWeight:    600,
                color:         'var(--text-primary)',
                margin:        '0 0 1rem',
                fontSize:      'var(--text-label)',
              }}
            >
              Vitais com rating "poor", últimas 20 ocorrências
            </p>

            {poorRows.length === 0 ? (
              <p
                style={{
                  color:      'var(--text-secondary)',
                  fontSize:   'var(--text-caption)',
                  margin:     0,
                  fontStyle:  'italic',
                }}
              >
                Nenhuma ocorrência "poor" registrada.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width:           '100%',
                    borderCollapse:  'collapse',
                    fontSize:        'var(--text-caption)',
                    color:           'var(--text-secondary)',
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: '1px solid var(--border-subtle)',
                        textAlign:    'left',
                      }}
                    >
                      {['Métrica', 'Valor', 'Rating', 'URL', 'Data'].map(col => (
                        <th
                          key={col}
                          style={{
                            padding:       '0.5rem 0.75rem',
                            fontWeight:    600,
                            color:         'var(--text-primary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize:      '11px',
                            whiteSpace:    'nowrap',
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {poorRows.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom:    '1px solid var(--border-subtle)',
                          backgroundColor: idx % 2 === 1 ? 'rgba(0,0,0,0.02)' : 'transparent',
                        }}
                      >
                        <td style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                          {row.name}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' }}>
                          {formatValue(row.name, row.value)}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' }}>
                          <RatingBadge rating={row.rating} />
                        </td>
                        <td
                          style={{
                            padding:      '0.5rem 0.75rem',
                            maxWidth:     '280px',
                            overflow:     'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace:   'nowrap',
                          }}
                          title={row.url}
                        >
                          {row.url}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                          {new Date(row.created_at).toLocaleString('pt-BR', {
                            day:    '2-digit',
                            month:  '2-digit',
                            hour:   '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
